import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { InfoIcon } from 'lucide-react';
export function UserManagement() {
  const [activeTab, setActiveTab] = useState<'list' | 'add'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    nid: '',
    role: '',
    station: '',
    permissions: {
      approveBeneficiaries: false,
      issuePermits: false,
      manageVessels: false,
      fileCases: false
    }
  });
  const users = [{
    name: 'Rahim Uddin',
    role: 'Range Officer',
    station: 'Chandpai Range',
    status: 'active' as const,
    lastActive: '2024-03-30 10:30'
  }, {
    name: 'Karim Sheikh',
    role: 'Station Officer',
    station: 'Katka Station',
    status: 'active' as const,
    lastActive: '2024-03-30 09:15'
  }, {
    name: 'Fatema Begum',
    role: 'Field Officer',
    station: 'Dhangmari Station',
    status: 'active' as const,
    lastActive: '2024-03-30 08:45'
  }, {
    name: 'Abdul Hakim',
    role: 'HQ Admin',
    station: 'Headquarters',
    status: 'active' as const,
    lastActive: '2024-03-29 16:20'
  }, {
    name: 'Jamal Hossain',
    role: 'Field Officer',
    station: 'Harbaria Station',
    status: 'suspended' as const,
    lastActive: '2024-03-25 14:30'
  }];
  const columns = [{
    key: 'name',
    label: 'Name'
  }, {
    key: 'role',
    label: 'Role'
  }, {
    key: 'station',
    label: 'Station/Range'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2133" />
  }, {
    key: 'lastActive',
    label: 'Last Active'
  }];
  const filters = [{
    key: 'role',
    label: 'Filter by Role',
    options: ['HQ Admin', 'Range Officer', 'Station Officer', 'Field Officer']
  }, {
    key: 'station',
    label: 'Filter by Station',
    options: ['Chandpai Range', 'Katka Station', 'Dhangmari Station', 'Harbaria Station']
  }, {
    key: 'status',
    label: 'Filter by Status',
    options: ['Active', 'Suspended', 'Inactive']
  }];
  const wizardSteps = [{
    title: 'Basic Info',
    description: 'Enter user basic information'
  }, {
    title: 'Role Assignment',
    description: 'Assign role and permissions'
  }, {
    title: 'Review & Confirm',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-2134">
      <div data-id="element-2135">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2136">User Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2137">
          Manage system users and their roles
        </p>
      </div>

      {/* Role-based visibility info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2138">
        <InfoIcon className="text-blue-600 flex-shrink-0 mt-0.5" size={20} data-id="element-2139" />
        <div className="text-sm text-blue-900" data-id="element-2140">
          <strong data-id="element-2141">Role-Based Access:</strong> Users will only see features and
          data relevant to their assigned role and station.
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2142">
        <div className="flex gap-4" data-id="element-2143">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2144">
            User List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2145">
            Add User
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'list' ? <DataTable columns={columns} data={users} filters={filters} onView={row => console.log('View', row)} onEdit={row => console.log('Edit', row)} searchPlaceholder="Search users..." data-id="element-2146" /> : <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.fullName || !formData.email || !formData.role) {
        alert('Please fill in all required fields.');
        return;
      }
      alert(`User ${formData.fullName} created successfully!`);
      setCurrentStep(0);
      setActiveTab('list');
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        nid: '',
        role: '',
        station: '',
        permissions: {
          approveBeneficiaries: false,
          issuePermits: false,
          manageVessels: false,
          fileCases: false
        }
      });
    }} data-id="element-2147">
          {currentStep === 0 && <div className="space-y-4" data-id="element-2148">
              <div className="grid grid-cols-2 gap-4" data-id="element-2149">
                <div data-id="element-2150">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2151">
                    Full Name *
                  </label>
                  <input type="text" required value={formData.fullName} onChange={e => setFormData({
              ...formData,
              fullName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2152" />
                </div>
                <div data-id="element-2153">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2154">
                    Email *
                  </label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2155" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2156">
                <div data-id="element-2157">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2158">
                    Phone Number *
                  </label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2159" />
                </div>
                <div data-id="element-2160">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2161">
                    NID Number *
                  </label>
                  <input type="text" required value={formData.nid} onChange={e => setFormData({
              ...formData,
              nid: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2162" />
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-2163">
              <div data-id="element-2164">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2165">
                  Role *
                </label>
                <select required value={formData.role} onChange={e => setFormData({
            ...formData,
            role: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2166">
                  <option value="" data-id="element-2167">Select Role</option>
                  <option value="hq" data-id="element-2168">HQ Admin</option>
                  <option value="range" data-id="element-2169">Range Officer</option>
                  <option value="station" data-id="element-2170">Station Officer</option>
                  <option value="field" data-id="element-2171">Field Officer</option>
                </select>
              </div>
              <div data-id="element-2172">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2173">
                  Assigned Station/Range *
                </label>
                <select required value={formData.station} onChange={e => setFormData({
            ...formData,
            station: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2174">
                  <option value="" data-id="element-2175">Select Station/Range</option>
                  <option value="chandpai" data-id="element-2176">Chandpai Range</option>
                  <option value="katka" data-id="element-2177">Katka Station</option>
                  <option value="dhangmari" data-id="element-2178">Dhangmari Station</option>
                  <option value="harbaria" data-id="element-2179">Harbaria Station</option>
                </select>
              </div>
              <div data-id="element-2180">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2181">
                  Permissions
                </label>
                <div className="space-y-2" data-id="element-2182">
                  <label className="flex items-center gap-2" data-id="element-2183">
                    <input type="checkbox" checked={formData.permissions.approveBeneficiaries} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  approveBeneficiaries: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2184" />
                    <span className="text-sm text-gray-700" data-id="element-2185">
                      Approve Beneficiaries
                    </span>
                  </label>
                  <label className="flex items-center gap-2" data-id="element-2186">
                    <input type="checkbox" checked={formData.permissions.issuePermits} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  issuePermits: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2187" />
                    <span className="text-sm text-gray-700" data-id="element-2188">Issue Permits</span>
                  </label>
                  <label className="flex items-center gap-2" data-id="element-2189">
                    <input type="checkbox" checked={formData.permissions.manageVessels} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  manageVessels: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2190" />
                    <span className="text-sm text-gray-700" data-id="element-2191">
                      Manage Vessels
                    </span>
                  </label>
                  <label className="flex items-center gap-2" data-id="element-2192">
                    <input type="checkbox" checked={formData.permissions.fileCases} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  fileCases: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2193" />
                    <span className="text-sm text-gray-700" data-id="element-2194">File Cases</span>
                  </label>
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-2195">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2196">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2197">
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2198">
                  <div data-id="element-2199">
                    <span className="text-gray-600" data-id="element-2200">Name:</span>{' '}
                    <span className="font-medium" data-id="element-2201">
                      {formData.fullName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2202">
                    <span className="text-gray-600" data-id="element-2203">Email:</span>{' '}
                    <span className="font-medium" data-id="element-2204">
                      {formData.email || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2205">
                    <span className="text-gray-600" data-id="element-2206">Phone:</span>{' '}
                    <span className="font-medium" data-id="element-2207">
                      {formData.phone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2208">
                    <span className="text-gray-600" data-id="element-2209">NID:</span>{' '}
                    <span className="font-medium" data-id="element-2210">
                      {formData.nid || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2211">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2212">
                  Role & Permissions
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2213">
                  <div data-id="element-2214">
                    <span className="text-gray-600" data-id="element-2215">Role:</span>{' '}
                    <span className="font-medium capitalize" data-id="element-2216">
                      {formData.role || 'Not selected'}
                    </span>
                  </div>
                  <div data-id="element-2217">
                    <span className="text-gray-600" data-id="element-2218">Station:</span>{' '}
                    <span className="font-medium capitalize" data-id="element-2219">
                      {formData.station || 'Not selected'}
                    </span>
                  </div>
                  <div className="col-span-2" data-id="element-2220">
                    <span className="text-gray-600" data-id="element-2221">Permissions:</span>{' '}
                    <span className="font-medium" data-id="element-2222">
                      {Object.entries(formData.permissions).filter(([_, val]) => val).map(([key]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())).join(', ') || 'None selected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}